import { API_URL } from '../../config.js';
import { toApiError } from '../../lib/api-error';

export type Organization = {
  id: string;
  name: string;
  createdAt: string;
};

export type OrgStats = {
  memberCount: number;
  propertyCount: number;
  issueCount: number;
  integrationCount: number;
};

export type Member = {
  id: string;
  userId: string;
  role: 'admin' | 'member';
  createdAt: string;
  displayName: string | null;
  primaryEmail: string | null;
  isActive: boolean;
  mfaEnrolled: boolean;
};

export type PendingInvite = {
  id: string;
  email: string | null;
  role: 'admin' | 'member';
  createdAt: string;
  expiresAt: string;
  invitedBy: {
    displayName: string | null;
    primaryEmail: string | null;
  };
};

export type SettingsFetch = (
  input: string | URL | Request,
  init?: RequestInit
) => Promise<Response>;

export type SettingsRequestOptions = {
  apiUrl?: string;
  fetchFn?: SettingsFetch;
};

const defaultFetch: SettingsFetch = (input, init) => fetch(input, init);

export async function fetchOrganization(
  options: SettingsRequestOptions = {}
): Promise<Organization> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const response = await fetchFn(`${apiUrl}/org`, { credentials: 'include' });
  if (!response.ok) throw await toApiError(response, 'Failed to fetch organization');
  const data = (await response.json()) as { organization: Organization };
  return data.organization;
}

export async function fetchOrganizationStats(
  options: SettingsRequestOptions = {}
): Promise<OrgStats> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const response = await fetchFn(`${apiUrl}/org/stats`, { credentials: 'include' });
  if (!response.ok) throw await toApiError(response, 'Failed to fetch organization stats');
  const data = (await response.json()) as { stats: OrgStats };
  return data.stats;
}

export async function fetchOrganizationMembers(
  options: SettingsRequestOptions = {}
): Promise<{ items: Member[] }> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const response = await fetchFn(`${apiUrl}/org/members`, { credentials: 'include' });
  if (!response.ok) throw await toApiError(response, 'Failed to fetch members');
  return response.json() as Promise<{ items: Member[] }>;
}

export async function fetchPendingInvites(
  options: SettingsRequestOptions = {}
): Promise<{ items: PendingInvite[] }> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const response = await fetchFn(`${apiUrl}/org/invites/pending`, { credentials: 'include' });
  if (!response.ok) throw await toApiError(response, 'Failed to fetch pending invites');
  return response.json() as Promise<{ items: PendingInvite[] }>;
}
